"use client"

import type React from "react"
// import { Sparkles } from "lucide-react"
import { useEffect, useState } from "react"
import { SpotlightOverlay, HighlightOverlay } from "./Overlays" // Assuming overlays are in a separate file
import gifs from "../assets/icons/gifs.json"
import { useAuth } from "../context/AuthContext";
import { dictionary } from "../utils/dictionary";
export interface TutorialStep {
  id: string
  question_text: string
  description: string
  type: string
  options: string[] | null
  next_step: string | null
  required: boolean
  is_summary: boolean
  survey_id: string
  is_first_step: boolean
}

function getTargetElementId(stepId: string): string {
  return `${stepId}-target`
}

interface TutorialTooltipProps {
  step: TutorialStep | null
  onNext: () => void
  onClose: () => void
  isActive: boolean
  onPause: () => void
}

interface Position {
  top: number
  left: number
  arrow: "top" | "bottom" | "left" | "right"
  arrowOffset: number
}

export default function TutorialTooltip({ step, onNext, onClose, isActive, onPause }: TutorialTooltipProps) {
  const [position, setPosition] = useState<Position>({ top: 0, left: 0, arrow: "top", arrowOffset: 50 })
  const targetElementId = step ? getTargetElementId(step.id) : null
  const { language } = useAuth();

  useEffect(() => {
    if (!targetElementId || !isActive) return

    const updatePosition = () => {
      const targetElement = document.getElementById(targetElementId)
      if (!targetElement) return

      const rect = targetElement.getBoundingClientRect()
      const tooltipWidth = 360
      const tooltipHeight = 180
      const spacing = 20
      const margin = 16

      let top = 0
      let left = 0
      let arrow: "top" | "bottom" | "left" | "right" = "top"
      let arrowOffset = 50

      const targetCenterX = rect.left + rect.width / 2
      const targetCenterY = rect.top + rect.height / 2

      const spaceBelow = window.innerHeight - rect.bottom
      const spaceAbove = rect.top
      const spaceRight = window.innerWidth - rect.right
      const spaceLeft = rect.left

      if (spaceBelow >= tooltipHeight + spacing) {
        top = rect.bottom + spacing
        left = targetCenterX - tooltipWidth / 2
        arrow = "top"
      } else if (spaceAbove >= tooltipHeight + spacing) {
        top = rect.top - tooltipHeight - spacing
        left = targetCenterX - tooltipWidth / 2
        arrow = "bottom"
      } else if (spaceRight >= tooltipWidth + spacing) {
        top = targetCenterY - tooltipHeight / 2
        left = rect.right + spacing
        arrow = "left"
      } else if (spaceLeft >= tooltipWidth + spacing) {
        top = targetCenterY - tooltipHeight / 2
        left = rect.left - tooltipWidth - spacing
        arrow = "right"
      } else {
        top = rect.bottom + spacing
        left = targetCenterX - tooltipWidth / 2
        arrow = "top"
      }

      const maxLeft = window.innerWidth - tooltipWidth - margin
      const maxTop = window.innerHeight - tooltipHeight - margin

      const constrainedLeft = Math.max(margin, Math.min(maxLeft, left))
      const constrainedTop = Math.max(margin, Math.min(maxTop, top))

      if (arrow === "top" || arrow === "bottom") {
        const tooltipCenter = constrainedLeft + tooltipWidth / 2
        const offset = ((targetCenterX - tooltipCenter) / tooltipWidth) * 100 + 50
        arrowOffset = Math.max(10, Math.min(90, offset))
      } else {
        const tooltipCenter = constrainedTop + tooltipHeight / 2
        const offset = ((targetCenterY - tooltipCenter) / tooltipHeight) * 100 + 50
        arrowOffset = Math.max(10, Math.min(90, offset))
      }

      setPosition({ top: constrainedTop, left: constrainedLeft, arrow, arrowOffset })
    }

    let animationFrameId: number
    let attempts = 0
    const maxAttempts = 50

    const waitForElement = () => {
      const targetElement = document.getElementById(targetElementId)

      if (targetElement) {
        updatePosition()
      } else if (attempts < maxAttempts) {
        attempts++
        animationFrameId = requestAnimationFrame(waitForElement)
      }
    }

    waitForElement()

    const observer = new MutationObserver(() => {
      updatePosition()
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    })

    window.addEventListener("resize", updatePosition)
    window.addEventListener("scroll", updatePosition)

    return () => {
      cancelAnimationFrame(animationFrameId)
      observer.disconnect()
      window.removeEventListener("resize", updatePosition)
      window.removeEventListener("scroll", updatePosition)
    }
  }, [targetElementId, isActive])

  if (!step || !isActive) return null

  const isFinalStep = step.is_summary || !step.next_step
  const isFirstStep = step.id === "createSalesPage"

  const getArrowStyle = () => {
    if (position.arrow === "top" || position.arrow === "bottom") {
      return { left: `${position.arrowOffset}%` }
    } else {
      return { top: `${position.arrowOffset}%` }
    }
  }

  const arrowClasses = {
    top: "before:absolute before:-top-3 before:-translate-x-1/2 before:border-[12px] before:border-transparent before:border-b-white",
    bottom:
      "before:absolute before:-bottom-3 before:-translate-x-1/2 before:border-[12px] before:border-transparent before:border-t-white",
    left: "before:absolute before:-left-3 before:-translate-y-1/2 before:border-[12px] before:border-transparent before:border-r-white",
    right:
      "before:absolute before:-right-3 before:-translate-y-1/2 before:border-[12px] before:border-transparent before:border-l-white",
  }

  return (
    <>
      <SpotlightOverlay targetId={targetElementId} onClick={isFinalStep ? onClose : isFirstStep ? () => {} : onPause} />

      {targetElementId && <HighlightOverlay targetId={targetElementId} onClick={isFirstStep ? onNext : undefined} />}

      <div
        className={`fixed z-50 bg-white rounded-xl shadow-2xl w-full max-w-sm ${arrowClasses[position.arrow]}`}
        style={
          {
            top: `${position.top}px`,
            left: `${position.left}px`,
            "--arrow-position": `${position.arrowOffset}%`,
          } as React.CSSProperties
        }
      >
        <style>{`
          .fixed.z-50.bg-white::before {
            ${position.arrow === "top" || position.arrow === "bottom" ? `left: ${position.arrowOffset}%;` : `top: ${position.arrowOffset}%;`}
          }
        `}</style>

        {isFinalStep ? (
          <div className="p-5">
            <div className="flex justify-center mb-4">
              <img src={gifs.success} alt="Success" className="w-24 h-24" />
            </div>

            <h3 className="text-base font-bold text-gray-900 mb-2 text-center leading-snug">{dictionary[step.question_text][language]}</h3>
            <p className="text-gray-600 text-xs leading-relaxed text-center mb-5">{dictionary[step.description][language]}</p>

            <button
              onClick={onClose}
              className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-2.5 px-5 rounded-lg text-sm transition-all shadow-lg hover:shadow-xl"
            >
              {dictionary["finalize"][language]}
            </button>
          </div>
        ) : isFirstStep ? (
          <div className="p-5">
            <h3 className="text-base font-bold text-gray-900 mb-2 leading-snug">{dictionary[step.question_text][language]}</h3>

            <p className="text-gray-600 text-xs leading-relaxed">{dictionary[step.description][language]}</p>
          </div>
        ) : (
          <div className="p-5">
            <h3 className="text-base font-bold text-gray-900 mb-2 leading-snug">{dictionary[step.question_text][language]}</h3>

            <p className="text-gray-600 text-xs leading-relaxed mb-4">{dictionary[step.description][language]}</p>

            <div className="flex justify-end">
              <button
                onClick={onNext}
                className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-6 rounded-full text-sm transition-all shadow-md hover:shadow-lg"
              >
                {dictionary["understood"][language]}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
